import React, { useContext, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  SimulationAttempt,
  SimulationTranscripts,
} from "src/types/coaching/courses";
import { CourseContext, SimulationReportState } from "../../../context";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchDraftSimulation,
  fetchLearnerReport,
} from "src/redux/actions/coaching/simulation";
import {
  fetchLearningRecordSimulation,
  fetchTranscriptsForLearningSimulation,
} from "src/redux/actions/coaching/learners";

import styles from "./simulationReport.module.scss";
import { BannerView } from "src/components/banner";
import Thumbnail from "src/components/thumbnail";

import _ from "lodash";
import cx from "classnames";
import Text from "src/components/text";
import pluralize from "pluralize";
import { TabBar } from "src/components/tabbar";
import { TabSection as TabPanel } from "src/pages/dealRoom/pages/assets";
import Slider from "src/components/slider";
import Button from "src/components/button";
import {
  LearningRecordSimulation,
  SimulationParameterEvaluation,
  SimulationSubParameterEvaluation,
} from "src/types/coaching/learners";
import { TabSection } from "src/pages/coaching/callLibrary/preview";
import { CALL_TYPES, DIFFICULTY_CHOICES } from "src/constants/simulation";

import { FileService } from "src/utils/file.service";
import { getFullName } from "src/utils/utils";
import { Media } from "src/types/media";
import { CallRecordingPlayer } from "src/pages/coaching/components/simulation/view/simulator/player";
import { SimulationContext, SimulationContextProps } from "src/pages/coaching/components/simulation/view/context";

interface SimulationLearnerReportViewProps {
  simulationReport: SimulationReportState;
}

type Parameter = SimulationParameterEvaluation;

interface ScoringViewProps {
  parameters: SimulationParameterEvaluation[];
  // eslint-disable-next-line no-unused-vars
  onParameterClick: (parameter: SimulationParameterEvaluation) => void;
  reportLoaded: boolean;
}

interface RecordingViewProps {
  learner: SimulationAttempt;
  childLearningRecord?: string | null;
  simulation?: LearningRecordSimulation | null;
  reportLoaded: boolean;
  minimized?: boolean;
}

interface RecordingTranscriptsProps {
  learner: SimulationAttempt;
  simulation?: LearningRecordSimulation | null;
  transcripts?: SimulationTranscripts[];
  loading?: boolean;
}

interface TranscriptRowProps {
  speaker: {
    name: string;
    icon?: Media | null;
  };
  text: string;
  time: string | boolean;
}

export const SimulationLearnerReportView = ({
  simulationReport,
}: SimulationLearnerReportViewProps) => {
  const dispatch = useDispatch();
  const courseContext = useContext(CourseContext);
  // const [activeTab, setActiveTab] = useState(0);
  const [selectedParameter, setSelectedParameter] = useState<SimulationParameterEvaluation | null>(
    null,
  );

  const learner = simulationReport.attempt;

  const content = _.get(simulationReport, "content", {});
  const courseContent = _.get(content,'content',[]);
  const id = _.get(content, "content.id", "");
  const name = _.get(content, "content.name", "");
  const thumbnail = _.get(content, "content.banner", "");
  const duration = _.get(content, "content.duration", 0);
  const callType = useMemo(() => {
    const value = _.get(content, "content.callType", "");
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [content]);

  const simulation = useSelector((state: any) => {
    return (_.get(state, "coaching.simulations", {}))
  });
  const setIsSidebarCollapsed = courseContext.setIsSidebarCollapsed;
  const isSidebarCollapsed = courseContext.isSidebarCollapsed;
  const activeTab = courseContext.activeTab;
  const setActiveTab = courseContext.setActiveTab;
  const secondaryView = courseContext.secondaryView;
  const changeSecondaryView = courseContext.changeSecondaryView;

  const difficulty = useMemo(() => {
    const value = _.get(simulation, `simulation.${id}.item.difficulty`, "");
    return DIFFICULTY_CHOICES.find((item) => item.value === value)?.label || "";
  }, [simulation, id]);

  const report = useMemo(
    () => _.get(simulation, "learnerReport.item.report", null),
    [simulation],
  );

  const learnerReport = useMemo(
    () => _.get(simulation, "learnerReport.item", null),
    [simulation],
  );

  const childLearningRecord = useMemo(() => {
    return _.get(simulationReport, "session.childLearningRecord", null);
  }, [simulationReport]);

  const reportLoaded = _.get(simulation, 'learnerReport.loaded', false);

  const parameters = report?.parameters ?? [];

  useEffect(() => {
    if (!id) return;
    dispatch(fetchDraftSimulation(id));
  }, [id]);

  useEffect(() => {
    if (learner && childLearningRecord) {
      dispatch(
        fetchLearnerReport({
          id: childLearningRecord,
        }),
      );
    }
  }, [learner?.id, childLearningRecord]);


  const handleParameterClick = (parameter: Parameter) => {
    setSelectedParameter(parameter);
  };

  const handleSliderClose = () => {
    setSelectedParameter(null);
  };

  const handleActiveTab = (index: number) => {
    if (index === 1) {
      setIsSidebarCollapsed?.(true);
      changeSecondaryView?.("call-recording");
    } else if (secondaryView === "call-recording") {
      changeSecondaryView?.(null);
    }
    setActiveTab?.(index);
  };

  const loading = useSelector((state: any) => {
    if (!childLearningRecord) return false;
    return _.get(
      state.learners,
      `simulation[${childLearningRecord}].transcripts.fetching`,
      false,
    );
  });

  const transcripts = useSelector((state: any) => {
    if (!childLearningRecord) return [];
    return _.get(
      state.learners,
      `simulation[${childLearningRecord}].transcripts.item`,
      [],
    );
  });

  const showTranscript = isSidebarCollapsed && activeTab === 1 && secondaryView !== "call-recording-minimized";
  const showMinimizedRecording = secondaryView === "call-recording-minimized";

  if(!courseContent) {
    return (
      <NoSimulation />
    )
  }

  return (
    <div className={styles.container}>
      <div className={cx(styles.detailsWrapper, { [styles.collapsedDetailsView]: showTranscript })}>
      <div className={styles.header}>
        {!name && <BannerLoader /> }
        {name && (
          <Banner name={name} thumbnail={thumbnail} callType={callType} />
        )}
        <SimulationMetadata
          duration={duration}
          callType={callType}
          difficulty={difficulty}
        />
      </div>
      <div className={styles.body}>
        <div className={styles.tabsWrapper}>
          <TabBar
            className={cx(styles.tabs)}
            gap={32}
            tabSize="equal"
            active={activeTab}
            onSelectionChange={(index)=>handleActiveTab(index)}
            innerClass={styles.innerClass}
          >
            <span className={activeTab === 0 ? styles.activeTab : ""}>
              scoring
            </span>
            <span className={activeTab === 1 ? styles.activeTab : ""}>
              recording
            </span>
            <span className={activeTab === 2 ? styles.activeTab : ""}>
              details
            </span>
          </TabBar>
        </div>
        <TabPanel index={0} selected={activeTab!}>
          <ScoringView
            parameters={parameters}
            onParameterClick={handleParameterClick}
            reportLoaded={reportLoaded}
          />
        </TabPanel>
        <TabPanel index={1} selected={activeTab!}>
          {activeTab === 1 && (
            <RecordingView
              learner={learner}
              childLearningRecord={childLearningRecord}
              simulation={learnerReport}
              reportLoaded={reportLoaded}
              minimized={false}
            />
          )}
        </TabPanel>
        <TabPanel index={2} selected={activeTab!}>
          {activeTab === 2 && (
            <SimulationDetails
              reportLoaded={reportLoaded}
              simulationReport={simulationReport}
            />
          )}
        </TabPanel>
      </div>
      </div>
      { showTranscript && (
        <>
          <div className={styles.transcriptDivider} />
          <RecordingTranscripts
            learner={learner}
            simulation={learnerReport}
            transcripts={transcripts}
            loading={loading}
          />
        </>)
      }
      {showMinimizedRecording && (
        <RecordingView
          learner={learner}
          childLearningRecord={childLearningRecord}
          simulation={learnerReport}
          reportLoaded={reportLoaded}
          minimized
        />
      )}
      <ParameterDetailSlider
        parameter={selectedParameter}
        onClose={handleSliderClose}
      />
    </div>
  );
};

interface ParameterDetailSliderProps {
  parameter: Parameter | null;
  onClose: () => void;
}

const ParameterDetailSlider: React.FC<ParameterDetailSliderProps> = ({
  parameter,
  onClose,
}) => {
  return (
    <Slider.Dialog
      className={styles.parameterDetail}
      width="520px"
      show={!!parameter}
      onClose={onClose}
      placement="start"
    >
      <Slider.Header className={styles.slideHeader}>
        <div className={styles.sliderHeader}>
          <span className={styles.sliderTitle}>
            {parameter?.name}
            {parameter && (
              <div className={styles.sliderScore}>
                score - {Math.round(parameter.score)}/5
              </div>
            )}
          </span>
          <i className="paperflite-close" onClick={onClose} />
        </div>
      </Slider.Header>

      <Slider.Body className={styles.sliderBody}>
        <div className={styles.parameterScroll}>
          {parameter && <ParameterBreakdownView parameter={parameter} />}
        </div>
      </Slider.Body>

      <Slider.Actions>
        <Button variant="secondary" text="Close" onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface ParameterBreakdownViewProps {
  parameter: Parameter;
}

const ParameterBreakdownView: React.FC<ParameterBreakdownViewProps> = ({
  parameter,
}) => {
  const [activeSubTab, setActiveSubTab] = useState(0);
  const subParameters = parameter.parameters ?? [];

  return (
    <div className={styles.breakdownContainer}>
      <TabBar
        className={styles.breakdownTabs}
        gap={24}
        tabSize="equal"
        active={activeSubTab}
        onSelectionChange={(index) => setActiveSubTab(index)}
      >
        {subParameters.map((sub, index) => (
          <span
            key={sub.uuid}
            className={index === activeSubTab ? styles.activeTab : ""}
          >
            {sub.name} ({sub.score})
          </span>
        ))}
      </TabBar>

      {subParameters.map((subparameter, index) => (
        <TabSection
          key={subparameter.uuid}
          index={index}
          selected={activeSubTab}
        >
          <SubParameterContent subParameter={subparameter} />
        </TabSection>
      ))}
    </div>
  );
};

interface SubParameterContentProps {
  subParameter: SimulationSubParameterEvaluation;
}

const SubParameterContent: React.FC<SubParameterContentProps> = ({
  subParameter,
}) => {
  const isEmpty =
    _.isEmpty(subParameter.scenarios) &&
    _.isEmpty(subParameter.positives) &&
    _.isEmpty(subParameter.suggestions) &&
    _.isEmpty(subParameter.missedOpportunities) &&
    (!subParameter.score || subParameter.score === 0);

  if (isEmpty) {
    return (
      <div className={styles.noData}>
        <div className={styles.noDataText}>No scenario found</div>
        <span className={styles.noDataSubText}>
          There were no relevant scenarios in the call to evaluate this
          parameter.
        </span>
      </div>
    );
  }

  return (
    <div className={styles.subParameterContent}>
      {subParameter.description && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>Description</div>
          <div className={styles.fieldValue}>{subParameter.description}</div>
        </div>
      )}

      <div className={styles.field}>
        <div className={styles.fieldLabel}>Score</div>
        <div className={styles.fieldValue}>
          {String(subParameter.score).padStart(2, "0")}
        </div>
      </div>

      {_.size(subParameter.scenarios) > 0 && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>Scenario</div>
          <div className={styles.fieldValue}>
            {subParameter.scenarios.map((scenario, i) => (
              <p key={i}>{scenario.description}</p>
            ))}
          </div>
        </div>
      )}

      {subParameter.positives && _.size(subParameter.positives) > 0 && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>Positives</div>
          <div className={styles.fieldValue}>{subParameter.positives}</div>
        </div>
      )}

      {subParameter.suggestions && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>Suggestions</div>
          <div className={styles.fieldValue}>{subParameter.suggestions}</div>
        </div>
      )}

      {_.size(subParameter.missedOpportunities) > 0 && (
        <div className={styles.field}>
          <div className={styles.fieldLabel}>Missed opportunities</div>
          <div className={styles.fieldValue}>
            <ul className={styles.list}>
              {subParameter.missedOpportunities.map((item, i) => (
                <li key={i}>
                  <p>{item}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Recording View ──────────────────────────────────────────────────────────

const RecordingView: React.FC<RecordingViewProps> = ({
  learner,
  childLearningRecord,
  simulation,
  reportLoaded,
  minimized = false,
}) => {
  const report = simulation?.report;
  const recordingUrl = report?.callRecordingUrl;
  const dispatch = useDispatch();
  const courseContext = useContext(CourseContext);

  const [simulationContext, setSimulationContext] = useState<SimulationContextProps>({
    view: "call-recording",
    secondaryView: minimized ? "call-recording-minimized" : "call-recording",
    changeSecondaryView: (view) => {
      courseContext.changeSecondaryView?.(view);
      setSimulationContext((prev) => ({ ...prev, secondaryView: view }));

      if (view === "call-recording") {
        courseContext.setActiveTab?.(1);
        courseContext.setIsSidebarCollapsed?.(true);
      }

      if (view === "call-recording-minimized") {
        courseContext.setActiveTab?.(0);
        courseContext.setIsSidebarCollapsed?.(true);
      }
    },
  });

  useEffect(() => {
    if (!childLearningRecord) return;
    dispatch(fetchLearningRecordSimulation(childLearningRecord));
    dispatch(fetchTranscriptsForLearningSimulation(childLearningRecord));
  }, [dispatch, childLearningRecord]);

  if (!reportLoaded) return <SimulationReportSkeleton tab={1} />;

  if (
    !simulation ||
    learner?.status !== "COMPLETED" ||
    report?.status === "in_sufficient_duration" ||
    !recordingUrl
  ) {
    return (
      <div className={styles.emptyMessage}>
        <h1>Recording unavailable</h1>
        <span>try again</span>
      </div>
    );
  }
  return (
    <div
      className={cx(styles.recordingPlayerSlot, {
        [styles.minimizedRecordingDock]: minimized,
      })}
    >
      <SimulationContext.Provider value={simulationContext}>
        <CallRecordingPlayer
          simulation={simulation}
          minimized={minimized}
          className={styles.callRecordingPlayer}
        />
      </SimulationContext.Provider>
    </div>
  );
};

export const RecordingTranscripts: React.FC<RecordingTranscriptsProps> = ({
  learner,
  simulation,
  transcripts,
  loading,
}) => {
  const courseContext = useContext(CourseContext)
  const isSliderCollapsed = courseContext.isSidebarCollapsed;
  const learnerProfile = useMemo(() => {
    const profile = _.get(learner, "user");
    return {
      name: getFullName(profile),
      icon: _.get(profile, "icon"),
    };
  }, [learner]);

  const persona = useMemo(() => {
    return _.get(simulation, "simulation.persona", null);
  }, [simulation]);

  const personaProfile = useMemo(() => {
    return {
      name: persona?.name || "",
      icon: persona?.icon,
    };
  }, [persona]);

  if (loading && _.size(transcripts) === 0) {
    return (
      <div className={cx(styles.transcripts, {
        [styles.transcriptsSidebar]: isSliderCollapsed,
      })}>
        {_.times(4).map((_, index) => (
          <TranscriptRowPlaceholder key={index} />
        ))}
      </div>
    );
  }

  if (_.size(transcripts) === 0) {
    return (
      <div className={styles.recordingEmpty}>
        <span>Transcript unavailable</span>
      </div>
    );
  }

  return (
    <div className={cx(styles.transcripts, {
      [styles.transcriptsSidebar]: isSliderCollapsed,
    })}>
      {(transcripts || [])
        .filter((t) => t.message)
        .map((transcript, index) => {
          const speaker =
            transcript.role === "user" ? learnerProfile : personaProfile;
          return (
            <TranscriptRow
              key={index}
              speaker={speaker}
              text={transcript.message}
              time={
                transcript.secondsFromStart > 0 &&
                FileService.formatTime(transcript.secondsFromStart)
              }
            />
          );
        })}
    </div>
  );
};

const TranscriptRow: React.FC<TranscriptRowProps> = ({
  speaker,
  text,
  time,
}) => {
  return (
    <div className={styles.transcriptRow}>
      {time && <div className={styles.time}>{time}</div>}
      <div className={styles.speaker}>
        <Thumbnail
          className={styles.icon}
          media={speaker.icon}
          defaultImage="/images/assets/simulationThumbnail.jpg"
        />
        <div className={styles.name}>{speaker.name}</div>
      </div>
      <div className={styles.text}>{text}</div>
    </div>
  );
};

const TranscriptRowPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.transcriptRow, styles.placeholder)}>
      <div className={styles.speaker}>
        <div className={cx(styles.icon, styles.block)} />
        <div className={cx(styles.name, styles.block)} />
      </div>
      <div className={styles.text}>
        <div className={styles.block} />
        <div className={styles.block} />
      </div>
    </div>
  );
};

// ─── Scoring View ─────────────────────────────────────────────────────────────

export const ScoringView: React.FC<ScoringViewProps> = ({
  parameters,
  onParameterClick,
  reportLoaded
}) => {

  if(!reportLoaded) {
    return (
      <SimulationReportSkeleton tab={0} />
    )
  }

  if (!parameters || parameters.length === 0) {
    return (
      <div className={styles.emptyMessage}>
        <h1>No scoring data available</h1>
        <span>try again</span>
      </div>
    );
  }

  return (
    <div className={styles.scoringWrapper}>
      <div className={styles.scoringGrid}>
        {parameters.map((parameter) => (
          <div
            key={parameter.uuid}
            className={cx(styles.scoringCell, styles.scoringCellClickable)}
            onClick={() => onParameterClick(parameter)}
          >
            <div className={styles.scoringName}>{parameter.name}</div>

            <div className={styles.scoringBottom}>
              <div className={styles.scoringScore}>
                <span className={styles.scoreValue}>
                  {String(Math.round(parameter.score)).padStart(2, "0")}&nbsp;
                </span>

                <span className={styles.totalScore}>/ 05</span>
              </div>

              <div className={styles.rightArrowIcon}>
                <i className="paperflite-right-arrow" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SimulationDetails = ({
  reportLoaded,
  simulationReport,
}: {
  reportLoaded: boolean;
  simulationReport: SimulationReportState;
}) => {
  const simulation = useSelector((state: any) => {
    return (_.get(state, "coaching.simulations", {}))
  });

  const content = _.get(simulationReport, "content", "");
  const id = _.get(content, "content.id", "");
  const type = _.get(simulation, `simulation.${id}.item.type`, "");

  const organization = useMemo(
    () => _.get(simulation, `simulation.${id}.item.organization`, {}),
    [simulation],
  );
  const objective = useMemo(
    () => _.get(simulation, `simulation.${id}.item.objectives`, {}),
    [simulation],
  );
  const persona = useMemo(
    () => _.get(simulation, `simulation.${id}.item.persona`, {}),
    [simulation],
  );

  const description = useMemo(() => _.get(simulation, `simulation.${id}.item.description`) || "", [simulation]);
  const pitchScenario = useMemo(() => _.get(simulation, `simulation.${id}.item.pitchScenario`, ""), [simulation, id]);
  const pitchAudience = useMemo(() => _.get(simulation, `simulation.${id}.item.pitchAudience`, ""), [simulation, id]);
  const simulationType = useMemo(() => _.get(simulation, `simulation.${id}.item.type`, ""), [simulation, id]);

  if(!reportLoaded) {
    return <SimulationReportSkeleton tab={2} />
  }

  if (simulationType === 'elevator_pitch' ) {
    return (
      <div className={styles.simulationDetails}>
        <div className={styles.cards}>
          <div className={styles.objectivesCard}>
            <div className={styles.header}>
              <i className={cx(styles.icon, "paperflite-objectives")} />
              <div className={styles.name}>Pitch Scenario</div>
            </div>
            <div className={styles.description}>
              {pitchScenario || "no scenario provided"}
            </div>
          </div>
          <div className={styles.personaCard}>
            <div className={styles.header}>
              <i className={cx(styles.icon, "paperflite-user-settings")} />
              <div className={styles.name}>Pitch Audience</div>
            </div>
            <div className={styles.description}>
              {pitchAudience || "no audience provided"}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.simulationDetails}>
      <div className={styles.description}>
        <p className={styles.title}>Description</p>
        <div
          className={styles.descriptionContent}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(description),
          }}
        />
      </div>
      <div className={styles.cards}>
        <div className={styles.organizationCard}>
          <div className={styles.header}>
            <div className={styles.icon}>
              <i className="paperflite-buildings" />
            </div>
            <div className={styles.name}>
              {organization?.name || "no name"}
            </div>
          </div>
          <div className={styles.description}>
            {organization?.description || ""}
          </div>
        </div>
        <div className={styles.personaCard}>
          <div className={styles.header}>
            {persona?.icon && (
              <Thumbnail
                media={persona?.icon}
                defaultImage="/images/assets/simulationThumbnail.jpg"
                className={cx(styles.icon, styles.image)}
              />
            )}

            {!persona?.icon && (
              <i className={cx(styles.icon, "paperflite-user-settings")} />
            )}

            <div className={styles.name}>
              {persona?.name || "no name"}
            </div>
          </div>
          <div className={styles.description}>
            {persona?.description || ""}
          </div>
        </div>
        <div className={styles.objectivesCard}>
          <div className={styles.header}>
            <div className={styles.icon}>
              <i className="paperflite-objectives" />
            </div>
            <div className={styles.name}>Objectives</div>
          </div>
          <div className={styles.description}>
            {objective?.description || ""}
          </div>
        </div>
      </div>
    </div>
  );
};

interface SimulationReportSkeletonProps {
  tab: number;
}

const SimulationReportSkeleton: React.FC<SimulationReportSkeletonProps> = ({ tab }) => (
  <div className={styles.skeletonContainer}>
    {tab === 0 && <div className={styles.skeletonGrid}>
      {[1, 2, 3, 4, 5].map((_, i) => (
        <div key={i} className={styles.skeletonCell}>
          <div className={cx(styles.skeletonBlock, styles.cellTitle)} />
          <div className={cx(styles.skeletonBlock, styles.skillPills, styles.cellScore)} />
        </div>
      ))}
    </div>}
    {tab === 1 && <div className={cx(styles.skeletonBlock, styles.skeletonVideo)}>
    </div>
    }
    {tab === 2 && 
      <div className={styles.cardSkeletonGrid}>
        {[1, 2, 3].map((i) => (
          <div key={i} className={styles.cardSkeletonItem}>
            <div className={styles.cardSkeletonHeader}>
              <div className={cx(styles.skeletonBlock, styles.circle, styles.cardSkeletonAvatar)} />
              <div className={cx(styles.skeletonBlock, styles.cardSkeletonTitle)} />
            </div>
            <div className={styles.cardSkeletonLines}>
              {[100, 96, 92, 88, 50].map((w, j) => (
                <div key={j} className={styles.skeletonBlock} style={{ width: `${w}%` }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    }
  </div>
);

interface SimulationMetadataProps {
  duration: number;
  callType: string;
  difficulty: string;
}

const SimulationMetadata: React.FC<SimulationMetadataProps> = ({
  duration,
  callType,
  difficulty,
}) => {

  if (!difficulty) {
    return (
      <div className={cx(styles.simulationMetadata, styles.placeholder)}>
        {[1,2,3].map((w, i) => (
          <div
            key={i}
            className={cx(styles.metadata, styles.block, styles.pills)}
          />
        ))}
      </div>
    );
  }
  return (<div className={styles.simulationMetadata}>
    <div className={styles.metadata}>
      <i className="paperflite-clock" />
      <span>{duration} {pluralize("minute", duration)}</span>
    </div>
    <div className={styles.metadata}>
      <i className="paperflite-calendar" />
      <span>{_.startCase(_.toLower(callType))}</span>
    </div>
    <div className={styles.metadata}>
      <i className="paperflite-meter" />
      <span>{difficulty}</span>
    </div>
  </div>)
};

interface BannerProps {
  name: string;
  thumbnail: any;
  callType: string | null;
}

const Banner: React.FC<BannerProps> = ({ name, thumbnail, callType }) => (
  <div className={styles.banner}>
    <div className={cx(
      styles.thumbnail,
      callType === "Discovery Call" && styles.discovery,
      callType === "Cold Call" && styles.coldcall,
      callType === "Elevator Pitch" && styles.elevatorPitch,
    )}>
      <BannerView
        banner={thumbnail as any}
        defaultBanner="/images/assets/simulationThumbnail.jpg"
        overlay={false}
      />
    </div>
    <Text text={name} className={styles.name} lines={1} />
  </div>
);

const BannerLoader: React.FC = () => (
  <div className={styles.banner}>
    <div className={cx(styles.thumbnail, styles.placeholder, styles.block, styles.circle)} />
    <div className={cx(styles.placeholder, styles.block, styles.skillTitle)} />
  </div>
);

export const NoSimulation: React.FC = () => (
  <div className={styles.noSimulation}>
    <div className={styles.noSimulationIconWrap}>
      <img src="/images/assets/simulationThumbnail.jpg" alt="" className={styles.noSimulationImg} />
    </div>
    <h2 className={styles.noSimulationTitle}>Simulation not available</h2>
    <p className={styles.noSimulationDescription}>something went wrong</p>
  </div>
);